/**
 * DataLayer-only analytics helpers for the auth flow.
 *
 * Mirrors the old project's `src/lib/analytics.ts`:
 * - trackEvent  — pushes only to dataLayer (no Mixpanel)
 * - setUserContext — pushes user_context event to dataLayer
 *
 * Used exclusively in AuthContext / auth hooks.
 * For feature-level tracking (dataLayer + Mixpanel) use `@/utils/analytics`.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>
  }
}

// ─── DataLayer-only trackEvent ───────────────────────────────────────────────

/**
 * Push an event to the dataLayer for GTM/GA4 only.
 * Does NOT send to Mixpanel (auth events should not double-fire).
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>
): void {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    ...eventParams,
  })
}

// ─── User context for GA4/GTM ────────────────────────────────────────────────

/**
 * Push user context to dataLayer so GTM/GA4 can pick it up.
 * Call on login (with userId + userRole) and on logout (with undefined values).
 */
export function setUserContext(
  userId?: string | number,
  userRole?: string
): void {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'user_context',
    user_id: userId ? String(userId) : undefined,
    user_role: userRole,
  })
}
